import * as React from 'react';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Tooltip } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { useResponsiveBreakpoint } from '@vadiun/react-hooks';
import { NewTable, NewTableColumn } from 'shared/components/NewTable';
import { formatNumberToPrice } from 'shared/helpers';
import { useNotificationsContext } from 'shared/context/notificationContext';
import { useTravelCordinate } from 'app/viaje/services/TravelsCordinate';
import { TransferReservationManagmentModel } from '../../gestion/models/TransferReservationManagment';
import { TransferReservationPendingModel } from '../../pendiente/models/TransferReservationPending';
import { ViajeTrasladoPendienteTableTimesCell } from '../../pendiente/components/ViajeTrasladoPendienteTableTimesCell';
import Client from './list/client';
import Points from './list/points';
import ServiceType from './list/serviceType';
import Driver from './list/driver';
import ActionsItemCordinate from './list/actions';
import PassengersItem from 'app/viaje/components/shared/passengers';
import { ButtonDriverDraft } from './DriversNotesModal';
import { ChoferPartialModel } from 'app/chofer/models/Chofer';
import { CoordinateColumnVisibility } from '../../viajeTraslado/utils/coordinateStorage';
import ExpandableTextCell from 'shared/components/Table/ExpandableTextCell';

interface TableQueryData {
  data: TransferReservationPendingModel[];
  total: number;
  isLoading: boolean;
  error: Error | undefined;
  serverSideOptions: any;
}

interface Props {
  tableQuery: TableQueryData;
  handlePageChange: (currentPage: number) => void;
  idTravelSelected: number | undefined;
  setIdTravelSelected: React.Dispatch<React.SetStateAction<number | undefined>>;
  onLocationsDetails: (reservation: TransferReservationPendingModel) => void;
  onDelayNotify: (travel: TransferReservationPendingModel) => void;
  onEditReservation: (travel: TransferReservationPendingModel) => void;
  onShowDriverDetails: (driver: ChoferPartialModel, busVehicle?: any) => void;
  onReservationCopy: (reservation: TransferReservationManagmentModel) => void;
  onCancelReservation: (reservation: TransferReservationPendingModel) => void;
  onReservationShowDetails: (id: number) => React.ReactNode;
  onReservationCopyDetails: (
    reservation: TransferReservationPendingModel
  ) => void;
  onStartTravel: (selectedReservation: number, chofer: number) => void;
  onPreAssignDriver: (travel: TransferReservationPendingModel) => void;
  onAssignDriver: (
    travel: TransferReservationPendingModel,
    isContingency: boolean
  ) => void;
  onRemovePreAssigned: (selectedReservation: number) => void;
  onBusesPreAssign: (travel: TransferReservationPendingModel) => void;
  onDriverDraft: (draft: string, reservationId: number) => void;
  columnVisibility?: CoordinateColumnVisibility;
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  isExpanded: boolean;
  stickyHeaderTop?: string;
}

type CoordinateRow = TransferReservationPendingModel & {
  schedule: { arrival: any; exit: any; end: any };
  passengers: string[];
};

export function ViajeTrasladoCoordinarTable({
  tableQuery,
  handlePageChange,
  idTravelSelected,
  setIdTravelSelected,
  onLocationsDetails,
  onShowDriverDetails,
  onEditReservation,
  onReservationCopy,
  onCancelReservation,
  onReservationShowDetails,
  onReservationCopyDetails,
  onDelayNotify,
  onStartTravel,
  onAssignDriver,
  onPreAssignDriver,
  onRemovePreAssigned,
  onBusesPreAssign,
  onDriverDraft,
  columnVisibility,
  onChangePage,
  onChangeRowsPerPage,
  isExpanded,
  stickyHeaderTop
}: Props) {
  const { travelsTaked } = useNotificationsContext();
  const { takeOrLeaveTravelServiceApi } = useTravelCordinate();
  const snackbar = useSnackbar();
  const display = useResponsiveBreakpoint();

  const isMobileOrTablet =
    display === 'xs' || display === 'sm' || display === 'md';

  const email = localStorage.getItem('email') ?? '';

  const checkServiceType = (val: string) => (val === 'VIP' ? 1 : 0);

  const data: CoordinateRow[] = (tableQuery.data ?? [])
    .map((reservation) => {
      const departureMoment = moment(reservation.departureDatetime);
      return {
        ...reservation,
        _formattedDeparture: departureMoment.format('DD-MM HH:mm'),
        _departureTimestamp: departureMoment.valueOf(),
        schedule: {
          arrival: reservation.reservationDatetime,
          exit: reservation.departureDatetime,
          end: reservation.arrivalDateTime
        },
        passengers: reservation.locations
          .map((location) =>
            location.passengers.map((passenger) => passenger.name)
          )
          .flat()
      };
    })
    .sort((a: any, b: any) => {
      // 1. Ubicar primero los que tienen edited_with_driver_assigned en true
      const driverAssignedDiff =
        +Boolean(b.edited_with_driver_assigned) -
        +Boolean(a.edited_with_driver_assigned);
      if (driverAssignedDiff !== 0) return driverAssignedDiff;
      // 2. Evaluar coincidencia exacta en formato 'DD-MM HH:mm'
      if (a._formattedDeparture === b._formattedDeparture) {
        return (
          +Boolean(b.hasPriority) - +Boolean(a.hasPriority) ||
          checkServiceType(b.serviceType) - checkServiceType(a.serviceType)
        );
      }
      // 3. Orden cronológico por fecha de salida
      return a._departureTimestamp - b._departureTimestamp;
    })
    .map(({ _formattedDeparture, _departureTimestamp, ...rest }) => rest); // Limpieza de propiedades auxiliares

  const getTravelTaked = (row: CoordinateRow) =>
    travelsTaked?.find((travel) => travel.transfer_reservation_id === row.id);

  const takeTravel = async (row: CoordinateRow) => {
    const response: any = await takeOrLeaveTravelServiceApi(row.id, true);
    if (response?.status === 400) {
      snackbar.enqueueSnackbar(response.message, { variant: 'error' });
      return;
    }
    setIdTravelSelected((prev) => (prev === row.id ? undefined : row.id));
  };

  const removeTakeTravel = async (row: CoordinateRow) => {
    const response: any = await takeOrLeaveTravelServiceApi(row.id, false);
    if (response?.status === 400) {
      snackbar.enqueueSnackbar(response.message, { variant: 'error' });
      return;
    }
    setIdTravelSelected(undefined);
  };

  const columns: NewTableColumn<CoordinateRow>[] = [
    {
      key: 'schedule',
      label: 'Horarios',
      width: '120px',
      render: (row) => (
        <ViajeTrasladoPendienteTableTimesCell
          isReserveForTheMoment={row.isReserveForTheMoment}
          editedWithDriverAssigned={row.edited_with_driver_assigned}
          schedule={row.schedule}
          hideDateIfToday
          priceDriver={`$${formatNumberToPrice(
            row?.estimated_prices?.driver_price ?? 0
          )}`}
        />
      )
    },
    {
      key: 'client_passengers',
      label: 'Cliente y Pasajeros',
      width: '180px',
      align: 'center',
      render: (row, index) => (
        <div className="flex flex-col gap-4 justify-center">
          <div className="text-sm font-medium">
            <Client current={row} isMobileOrTablet={false} />
          </div>
          <div className="text-xs text-gray-500 dark:text-inkMuted-dark">
            <PassengersItem passengers={row.passengers} idTravel={row.id} />
          </div>
        </div>
      )
    },
    {
      key: 'puntos',
      label: 'Domicilios',
      width: {
        '2xl': '280px'
      },
      align: 'center',
      render: (row, index) => (
        <Points
          data={data}
          index={index}
          isMobileOrTablet={isMobileOrTablet}
          onLocationsDetails={onLocationsDetails}
        />
      )
    },
    {
      key: 'group_observations',
      label: 'Observaciones',
      width: { '2xl': '180px', '3xl': '200px' },
      align: 'center',
      render: (row) => (
        <div className="flex flex-col gap-4 text-xs w-36">
          {row.coordinationObservation && (
            <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                Coord:{' '}
              </span>
              <ExpandableTextCell
                text={row.coordinationObservation}
                maxWidthClass="w-full"
                isMobileOrTablet={isMobileOrTablet}
              />
            </div>
          )}
          {row.general_observation && (
            <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                Chofer:{' '}
              </span>
              <ExpandableTextCell
                text={row.general_observation}
                maxWidthClass="w-full"
                isMobileOrTablet={isMobileOrTablet}
              />
            </div>
          )}
        </div>
      )
    },
    {
      key: 'serviceType',
      label: 'Servicio',
      width: '160px',
      render: (row, index) => (
        <ServiceType
          data={data}
          index={index}
          isMobileOrTablet={isMobileOrTablet}
        />
      )
    },
    {
      key: 'preAssignedDriver',
      label: 'Chofer',
      align: 'center',
      width: '160px',
      render: (row, index) => (
        <div className="flex h-full w-full flex-col justify-between gap-4">
          {!row.preAssignedDriver?.codigo && (
            <ButtonDriverDraft
              initialValue={row.preassigned_driver_draft ?? undefined}
              handleOpenModal={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDriverDraft(row.preassigned_driver_draft ?? '', row.id);
              }}
            />
          )}
          <Driver
            data={data}
            index={index}
            isMobileOrTablet={isMobileOrTablet}
            onShowDriverDetails={(driver, busVehicle) =>
              onShowDriverDetails(driver, busVehicle)
            }
          />
        </div>
      )
    },
    {
      key: 'group_additional',
      label: 'Información Adicional',
      width: { '2xl': '160px' },
      align: 'center',
      render: (row) => (
        <div className="flex flex-col gap-4 text-xs w-36">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-ink dark:text-ink-dark text-sm">
              {row.id}
            </span>
          </div>
          {row.platformId && (
            <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-400">Plataforma:</span>
              <ExpandableTextCell
                text={row.platformId}
                maxWidthClass="w-full"
                isMobileOrTablet={isMobileOrTablet}
              />
            </div>
          )}
          {row.event_name && (
            <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-400">Evento:</span>
              <ExpandableTextCell
                text={row.event_name}
                maxWidthClass="w-full"
                isMobileOrTablet={isMobileOrTablet}
              />
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'center',
      width: '100px',
      render: (row, index) => (
        <ActionsItemCordinate
          current={row}
          index={index}
          onAssignDriver={onAssignDriver}
          onCancelReservation={onCancelReservation}
          onDelayNotify={onDelayNotify}
          onEditReservation={onEditReservation}
          onPreAssignDriver={onPreAssignDriver}
          onRemovePreAssigned={onRemovePreAssigned}
          onReservationCopy={onReservationCopy}
          onReservationShowDetails={onReservationShowDetails}
          onReservationCopyDetails={onReservationCopyDetails}
          onStartTravel={onStartTravel}
          onBusesPreAssign={onBusesPreAssign}
        />
      )
    }
  ];

  const currentPage = (tableQuery.serverSideOptions?.page ?? 0) + 1;
  const currentRowsPerPage = tableQuery.serverSideOptions?.rowsPerPage ?? 100;

  return (
    <NewTable<CoordinateRow>
      columns={columns}
      columnVisibility={columnVisibility as unknown as Record<string, boolean>}
      data={data}
      getRowKey={(row) => row.id}
      isLoading={tableQuery.isLoading}
      onRowClick={takeTravel}
      renderRowBanner={(row) => {
        const taked = getTravelTaked(row);
        if (!taked) {
          return null;
        }
        const takedByOtherUser = taked.email !== email;
        if (takedByOtherUser) {
          return (
            <div className="flex w-fit items-center gap-2 rounded-t-lg bg-red-100 px-4 py-1 text-xs text-red-500 dark:bg-danger-dark/15 dark:text-danger-dark">
              <FontAwesomeIcon icon={faLock} size="1x" />
              <p>En uso por: {taked.name}</p>
            </div>
          );
        }
        return (
          <div className="flex w-fit items-center gap-2 rounded-t-lg bg-primary-100 px-4 py-1 text-xs text-gray-500 dark:bg-white/10 dark:text-inkMuted-dark">
            <PersonIcon style={{ fontSize: 14 }} />
            <p>En uso por: {taked.name}</p>
            <Tooltip title="Desasignar traslado">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTakeTravel(row);
                }}
              >
                <HighlightOffIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      }}
      getRowClassName={(row) => {
        const taked = getTravelTaked(row);
        const isSelected = idTravelSelected === row.id;
        const takenClass = taked
          ? taked.email !== email
            ? 'bg-red-50 dark:bg-danger-dark/10'
            : 'bg-primary-50 dark:bg-darkLight'
          : '';
        const selectedClass = isSelected ? 'ring-2 ring-secondary-400' : '';
        return `${takenClass} ${selectedClass}`.trim();
      }}
      page={currentPage}
      total={tableQuery.total}
      rowsPerPage={currentRowsPerPage}
      onChangePage={(newPage) => {
        onChangePage(newPage);
        handlePageChange(newPage);
      }}
      onChangeRowsPerPage={onChangeRowsPerPage}
      rowsPerPageOptions={[10, 30, 50, 100, 200, 300, 400, 500]}
      stickyHeader={{
        top: stickyHeaderTop ?? (isExpanded ? '360px' : '106px'),
        zIndex: 1
      }}
      emptyTitle="No hay viajes pendientes"
    />
  );
}
